import React, { useEffect, useState } from "react";
import api from "@/api";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtDate, fmtTime, todayISO, inr } from "@/lib/tps";
import { StatusBadge } from "@/components/StatusBadge";
import { Link } from "react-router-dom";
import { format, addDays } from "date-fns";
import DatePicker from "@/components/DatePicker";
import { useAuth } from "@/context/AuthContext";

export default function DeliveryPickup() {
  const [date, setDate] = useState(todayISO());
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    api.get(`/orders?start=${date}&end=${date}`).then((r) => setOrders(r.data.filter((o) => !["Cancelled", "Lost"].includes(o.status))));
  }, [date]);

  const deliveries = orders.filter((o) => o.delivery_method !== "Pickup");
  const pickups = orders.filter((o) => o.delivery_method === "Pickup");

  return (
    <div className="space-y-3 animate-fade-in" data-testid="delivery-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="font-heading text-2xl md:text-3xl">Delivery / Pickup</h1>
        <div className="flex items-center gap-2">
          <button className="text-xs text-[#B86F77] hover:underline" onClick={() => setDate(todayISO())} data-testid="dp-today-btn">Today</button>
          <button className="text-xs text-[#B86F77] hover:underline" onClick={() => setDate(format(addDays(new Date(), 1), "yyyy-MM-dd"))} data-testid="dp-tomorrow-btn">Tomorrow</button>
          <DatePicker value={date} onChange={setDate} testId="dp-date-picker" />
        </div>
      </div>

      <Tabs defaultValue="delivery">
        <TabsList className="bg-[#FCF9F9]">
          <TabsTrigger value="delivery" data-testid="tab-delivery">Deliveries ({deliveries.length})</TabsTrigger>
          <TabsTrigger value="pickup" data-testid="tab-pickup">Pickups ({pickups.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="delivery"><OrderTable rows={deliveries} /></TabsContent>
        <TabsContent value="pickup"><OrderTable rows={pickups} /></TabsContent>
      </Tabs>
    </div>
  );
}

function OrderTable({ rows }) {
  const { isOwner } = useAuth();
  if (rows.length === 0) return <Card className="border-[#E8E3DF]"><CardContent className="p-8 text-center text-[#4B5563]">No orders.</CardContent></Card>;
  return (
    <Card className="border-[#E8E3DF] overflow-hidden">
      <div className="overflow-x-auto">
        <Table className="tps-table">
          <TableHeader className="bg-[#FCF9F9]">
            <TableRow>
              <TableHead className="py-2">Time</TableHead>
              <TableHead>Order</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Address</TableHead>
              <TableHead>Maps</TableHead>
              <TableHead>Method</TableHead>
              {isOwner && <TableHead className="text-right">Balance</TableHead>}
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.sort((a, b) => (a.required_time || "").localeCompare(b.required_time || "")).map((o) => (
              <TableRow key={o.id} data-testid={`dp-row-${o.order_id}`}>
                <TableCell className="py-2 font-medium">{fmtTime(o.required_time)}</TableCell>
                <TableCell className="py-2"><Link to={`/orders/${o.id}`} className="text-[#B86F77] hover:underline">{o.order_id}</Link></TableCell>
                <TableCell className="py-2">{o.customer_name}<div className="text-[11px] text-[#4B5563]">{o.customer_phone}</div></TableCell>
                <TableCell className="py-2 text-sm">{o.delivery_address || "—"}</TableCell>
                <TableCell className="py-2 text-sm">
                  {o.google_maps_link ? (
                    <a href={o.google_maps_link} target="_blank" rel="noreferrer" className="text-[#B86F77] hover:underline" data-testid={`dp-maps-${o.order_id}`}>Open map</a>
                  ) : "—"}
                </TableCell>
                <TableCell className="py-2 text-sm">{o.delivery_method}</TableCell>
                {isOwner && <TableCell className="py-2 text-right text-sm">{inr(o.balance_amount)}</TableCell>}
                <TableCell className="py-2"><StatusBadge status={o.status} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}
